// The server keeps these tiers for historical renders, but rejects new jobs.
// Source: sogni-socket/constants/retiredModels.js.
export const RETIRED_IMAGE_MODEL_IDS = new Set([
  'flux2_dev_fp8',
  'flux1-krea-dev_fp8_scaled',
  'flux1-dev-kontext_fp8_scaled'
]);

// These workflows belong to dedicated image actions, not prompt generation.
const IMAGE_UTILITY_IDS = new Set([
  'birefnet_image_background_removal_fp16',
  'sam3_image_segment_bf16'
]);

// Source: sogni-socket/services/contentRatingService.js + appMlxWorker.js.
export const MATURE_IMAGE_MODEL_IDS = new Set([
  'dark_beast_z_image_turbo_v9_bf16',
  'dark_beast_krea2_fp8',
  'dark_beast_krea2_identity_edit_v1_2',
  'dark_beast_z_image_turbo_8bit',
  'dark_beast_z_image_turbo_4bit',
  'dark_beast_krea2_4bit'
]);

export function isImageGenerator(model) {
  return model?.mediaType === 'image'
    && !RETIRED_IMAGE_MODEL_IDS.has(model.id)
    && !IMAGE_UTILITY_IDS.has(model.id)
    && model.parameters?.isUpscale !== true;
}

export function getImageModelFamily(model) {
  const label = model.name || model.id;
  const knownFamily = label.match(/^(?:Dark Beast )?(Qwen Image|Krea ?2|Z-Image|Chroma(?:1)?|FLUX[. ]?1|GPT Image)/i);
  if (knownFamily) return knownFamily[1].replace(/^Krea ?2$/i, 'Krea 2');
  // Group precision/resolution variants of a community checkpoint together.
  return label.replace(/\s+\[(?:MLX )?\d+\s?bit\]/gi, '')
    .replace(/\s+\d+px/gi, '').trim();
}

export function normalizeImageCatalog(snapshot) {
  const data = snapshot?.data ?? snapshot;
  if (!Array.isArray(data?.models)) throw new Error('The image catalog could not be read.');
  const seen = new Set();
  return data.models.filter(isImageGenerator).filter((model) => {
    if (typeof model.id !== 'string' || !model.id || seen.has(model.id)) return false;
    seen.add(model.id);
    return true;
  }).map((model) => ({
    ...model,
    label: model.name || model.id,
    family: getImageModelFamily(model),
    tags: Array.isArray(model.tags) ? model.tags : [],
    availableNetworks: Array.isArray(model.availableNetworks) ? model.availableNetworks : [],
    workerCounts: model.workerCounts || {},
    requiresMatureContent: MATURE_IMAGE_MODEL_IDS.has(model.id),
    requiresReference: model.parameters?.requiresContextImage === true,
    acceptsReference: model.parameters?.requiresContextImage === true || Number(model.parameters?.maxContextImages) > 0,
    isPartner: model.parameters?.isExternalAPI === true,
    isPremiumOnly: model.parameters?.isExternalAPI === true && model.parameters?.premiumOnly === true,
    freeSparkEligible: model.tags?.includes('free-tier') === true
  })).sort((a, b) => a.label.localeCompare(b.label));
}

export function getImageModelCoverage(model, isUnlimited = false) {
  if (!model?.parameters) return 'unknown';
  if (model?.isPremiumOnly) return 'credits';
  return isUnlimited ? 'covered' : 'credits';
}

export function filterImageCatalog(models, {
  query = '', family = '', workflow = '', coverage = '', network = 'fast',
  favoritesOnly = false, favoriteIds = [], selectedIds = [],
  showUnavailable = false, isUnlimited = false
} = {}) {
  const search = query.trim().toLowerCase();
  return models.filter((model) => {
    if (!showUnavailable && !model.availableNetworks.includes(network) && !selectedIds.includes(model.id)) return false;
    if (family && model.family !== family) return false;
    if (workflow === 'edit' && !model.acceptsReference && !model.isPartner) return false;
    if (workflow === 'text' && model.requiresReference) return false;
    if (coverage && getImageModelCoverage(model, isUnlimited) !== coverage) return false;
    if (favoritesOnly && !favoriteIds.includes(model.id)) return false;
    if (search && ![model.label, model.id, model.family, model.attribution?.creator, ...model.tags]
      .filter(Boolean).join(' ').toLowerCase().includes(search)) return false;
    return true;
  });
}

/** Keep exact saved selections while availability changes or the catalog loads. */
export function normalizePersistedImageModelIds(ids, fallbackIds, supportedIds = null) {
  const normalized = Array.isArray(ids) ? [...new Set(ids.filter((id) => (
    typeof id === 'string' && id.length > 0 && !RETIRED_IMAGE_MODEL_IDS.has(id)
      && !IMAGE_UTILITY_IDS.has(id) && id !== 'rtx_vsr_pro'
      && (!supportedIds || supportedIds.has(id))
  )))] : [];
  return normalized.length > 0 ? normalized : [...fallbackIds];
}
